import { call, put, select, takeEvery } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../reducers';
import { partTercAttributesApi, PartTercAttribute, PartTercAttributeSearchParams } from '../../services/partTercAttributesApi';
import {
  setLoading, setError, setItems, setSelectedItem, addItem, updateItem, removeItem,
  setValidationErrors, setPagination, setStatistics, setBulkOperationLoading, setSuccessMessage,
  FETCH_PART_TERC_ATTRIBUTES, FETCH_PART_TERC_ATTRIBUTE_BY_ID, CREATE_PART_TERC_ATTRIBUTE, UPDATE_PART_TERC_ATTRIBUTE, DELETE_PART_TERC_ATTRIBUTE,
  SEARCH_PART_TERC_ATTRIBUTES, FETCH_PART_TERC_ATTRIBUTES_STATS, BULK_PART_TERC_ATTRIBUTES_ACTIVATE, BULK_PART_TERC_ATTRIBUTES_DEACTIVATE,
  BULK_PART_TERC_ATTRIBUTES_DELETE, EXPORT_PART_TERC_ATTRIBUTES_CSV,
} from '../reducers/partTercAttributesSlice';

function* fetchSaga(action: PayloadAction<PartTercAttributeSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true)); yield put(setError(null));
    const state: RootState = yield select();
    const { pagination, searchParams } = state.partTercAttributes;
    const params = { page: pagination.page, pageSize: pagination.pageSize, ...searchParams, ...action.payload };
    const resp = yield call(partTercAttributesApi.getAll, params);
    yield put(setItems(resp.data));
    yield put(setPagination({ total: resp.total, page: params.page || 1, pageSize: params.pageSize || 10 }));
  } catch (e: any) { yield put(setError(e.message || 'Failed to fetch Part Tertiary Attributes')); }
  finally { yield put(setLoading(false)); }
}

function* fetchByIdSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try { yield put(setLoading(true)); yield put(setError(null)); const item = yield call(partTercAttributesApi.getById, action.payload); yield put(setSelectedItem(item)); }
  catch (e: any) { yield put(setError(e.message || 'Failed to fetch Part Tertiary Attribute')); }
  finally { yield put(setLoading(false)); }
}

function* createSaga(action: PayloadAction<Omit<PartTercAttribute, 'Id'>>): Generator<any, void, any> {
  try {
    yield put(setLoading(true)); yield put(setError(null));
    const errors = yield call(partTercAttributesApi.validation.validate, action.payload);
    if (errors.length) { yield put(setValidationErrors(errors)); return; }
    const created = yield call(partTercAttributesApi.create, action.payload);
    yield put(addItem(created)); yield put(setSuccessMessage('Created successfully'));
  } catch (e: any) { yield put(setError(e.message || 'Failed to create Part Tertiary Attribute')); }
  finally { yield put(setLoading(false)); }
}

function* updateSaga(action: PayloadAction<{ id: number; item: Partial<PartTercAttribute> }>): Generator<any, void, any> {
  try {
    yield put(setLoading(true)); yield put(setError(null));
    const { id, item } = action.payload;
    const errors = yield call(partTercAttributesApi.validation.validate, item);
    if (errors.length) { yield put(setValidationErrors(errors)); return; }
    const updated = yield call(partTercAttributesApi.update, id, item);
    yield put(updateItem(updated)); yield put(setSuccessMessage('Updated successfully'));
  } catch (e: any) { yield put(setError(e.message || 'Failed to update Part Tertiary Attribute')); }
  finally { yield put(setLoading(false)); }
}

function* deleteSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true)); yield put(setError(null));
    const resp = yield call(partTercAttributesApi.delete, action.payload);
    if (resp.success) { yield put(removeItem(action.payload)); yield put(setSuccessMessage('Deleted successfully')); }
    else { yield put(setError(resp.message || 'Failed to delete')); }
  } catch (e: any) { yield put(setError(e.message || 'Failed to delete Part Tertiary Attribute')); }
  finally { yield put(setLoading(false)); }
}

function* searchSaga(action: PayloadAction<PartTercAttributeSearchParams>): Generator<any, void, any> {
  try {
    yield put(setLoading(true)); yield put(setError(null));
    const resp = yield call(partTercAttributesApi.search, action.payload);
    yield put(setItems(resp.data));
    yield put(setPagination({ total: resp.total, page: action.payload.page || 1, pageSize: action.payload.pageSize || 10 }));
  } catch (e: any) { yield put(setError(e.message || 'Failed to search Part Tertiary Attributes')); }
  finally { yield put(setLoading(false)); }
}

function* statsSaga(): Generator<any, void, any> {
  try { const stats = yield call(partTercAttributesApi.getStatistics); yield put(setStatistics(stats)); } catch (_) {}
}

function* bulkActivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try { yield put(setBulkOperationLoading(true)); const resp = yield call(partTercAttributesApi.bulkActivate, action.payload); if (resp.success) { yield put(setSuccessMessage(resp.message || 'Activated successfully')); yield put({ type: FETCH_PART_TERC_ATTRIBUTES }); } else { yield put(setError(resp.message || 'Failed to activate')); } }
  finally { yield put(setBulkOperationLoading(false)); }
}
function* bulkDeactivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try { yield put(setBulkOperationLoading(true)); const resp = yield call(partTercAttributesApi.bulkDeactivate, action.payload); if (resp.success) { yield put(setSuccessMessage(resp.message || 'Deactivated successfully')); yield put({ type: FETCH_PART_TERC_ATTRIBUTES }); } else { yield put(setError(resp.message || 'Failed to deactivate')); } }
  finally { yield put(setBulkOperationLoading(false)); }
}
function* bulkDeleteSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try { yield put(setBulkOperationLoading(true)); const resp = yield call(partTercAttributesApi.bulkDelete, action.payload); if (resp.success) { yield put(setSuccessMessage(resp.message || 'Deleted successfully')); yield put({ type: FETCH_PART_TERC_ATTRIBUTES }); } else { yield put(setError(resp.message || 'Failed to delete')); } }
  finally { yield put(setBulkOperationLoading(false)); }
}

function* exportCsvSaga(action: PayloadAction<PartTercAttributeSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    const state: RootState = yield select();
    const params = { ...state.partTercAttributes.searchParams, ...action.payload };
    const blob = yield call(partTercAttributesApi.exportToCSV, params);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `part_terc_attributes_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
    yield put(setSuccessMessage('Exported successfully'));
  } catch (e: any) { yield put(setError(e.message || 'Failed to export Part Tertiary Attributes')); }
  finally { yield put(setLoading(false)); }
}

export function* partTercAttributesSaga(): Generator<any, void, any> {
  yield takeEvery(FETCH_PART_TERC_ATTRIBUTES, fetchSaga);
  yield takeEvery(FETCH_PART_TERC_ATTRIBUTE_BY_ID, fetchByIdSaga);
  yield takeEvery(CREATE_PART_TERC_ATTRIBUTE, createSaga);
  yield takeEvery(UPDATE_PART_TERC_ATTRIBUTE, updateSaga);
  yield takeEvery(DELETE_PART_TERC_ATTRIBUTE, deleteSaga);
  yield takeEvery(SEARCH_PART_TERC_ATTRIBUTES, searchSaga);
  yield takeEvery(FETCH_PART_TERC_ATTRIBUTES_STATS, statsSaga);
  yield takeEvery(BULK_PART_TERC_ATTRIBUTES_ACTIVATE, bulkActivateSaga);
  yield takeEvery(BULK_PART_TERC_ATTRIBUTES_DEACTIVATE, bulkDeactivateSaga);
  yield takeEvery(BULK_PART_TERC_ATTRIBUTES_DELETE, bulkDeleteSaga);
  yield takeEvery(EXPORT_PART_TERC_ATTRIBUTES_CSV, exportCsvSaga);
} 